import PropTypes from "prop-types"
import { api } from "utils/api"
import Icon from "components/planning_center_icon"
import { Menu, MenuButton, MenuList, MenuLink } from "@reach/menu-button"
import RegistrationButton from "./RegistrationButton"
import FooterDetails from "./FooterDetails"

const SectionFooter = ({
  event,
  registration,
  isActingStaff,
  hasAttendeeButton: _hasAttendeeButton = false,
  hasDiscountButton = false,
  hasDeleteButton = false,
  hasCancelButton = false,
  hasEmailButton = false,
  hasFinalizeButton = false
}) => {
  const hasInitialAttendees = registration.attendees.some(a => a.finalizationState === "initial")

  const handleCancel = () => {
    return alertModal(
      {
        title: "Cancel Registration?",
        text: "Any payments will not be automatically refunded.",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't cancel it",
        confirmButtonText: "Yes, cancel it!",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () => {
        api.post(registration.paths.cancel).then(_resp => {
          alertModal(
            {
              title: "Canceled!",
              text: "Registration has been canceled.",
              type: "success",
              confirmButtonText: "Ok",
              closeOnConfirm: true
            },
            () => Turbolinks.visit()
          )
        })
      }
    )
  }

  const handleRestore = () => {
    return alertModal(
      {
        title: "Restore Registration?",
        text: registration.restoreAlertText,
        showCancelButton: true,
        cancelButtonText: "No, don't restore it",
        confirmButtonText: "Yes, restore it!",
        closeOnConfirm: true,
        type: "warning"
      },
      () => {
        api.del(registration.paths.cancel).then(_resp => {
          alertModal(
            {
              title: "Restored!",
              text: "Registration has been restored.",
              type: "success",
              confirmButtonText: "Ok",
              closeOnConfirm: true
            },
            () => Turbolinks.visit()
          )
        })
      }
    )
  }

  const handleDelete = () => {
    return alertModal(
      {
        title: "Delete Registration?",
        text: "This cannot be undone.",
        customClass: "destructive-warning",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't delete it",
        confirmButtonText: "Yes, delete it!",
        closeOnConfirm: true
      },
      () => {
        api.del(registration.paths.show).then(_resp => {
          alertModal(
            {
              confirmButtonText: "Ok",
              type: "success",
              title: "Success",
              text: "Registration deleted successfully.",
              closeOnConfirm: true
            },
            () => Turbolinks.visit(event.paths.show)
          )
        })
      }
    )
  }

  const handleResendEmail = () => {
    return alertModal(
      {
        title: "Resend email?",
        text: "Do you want to resend the confirmation email?",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't send it",
        confirmButtonText: "Yes, send it!",
        closeOnConfirm: false
      },
      () => {
        api.post(registration.paths.confirmEmail).then(_resp => {
          alertModal({
            title: "Success!",
            type: "success",
            confirmButtonText: "Ok",
            closeOnConfirm: true
          })
        })
      }
    )
  }

  const handleFinalize = () => {
    return alertModal(
      {
        title: `Current Finalization Errors`,
        text: `Are you sure you want to finalize this registration?`,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't finalize it",
        confirmButtonText: "Finalize It",
        closeOnConfirm: true
      },
      () => {
        api.put(registration.paths.finalize).then(_ => {
          alertModal(
            {
              title: "Finalized!",
              text: "Registration has been finalized.",
              type: "success",
              confirmButtonText: "Ok",
              closeOnConfirm: true
            },
            () => Turbolinks.visit(registration.paths.show)
          )
        })
      }
    )
  }

  return (
    <div className="d-f@sm ai-c jc-sb mb-4">
      <div className="font-size-5 c-secondary mb-1 mb-0@sm mt-4p ws-nw mr-2">
        Registration #{registration.id}
        {event.isSimple && (
          <FooterDetails
            createdAt={registration.createdAt}
            createdByName={registration.createdByName}
            isCanceled={registration.isCanceled}
            canceledOn={registration.canceledOn}
          />
        )}
      </div>

      <div className="d-f ai-c fw-w" css={{ gap: "0.5rem" }}>
        {hasDiscountButton && !registration.isForFreeEvent && (
          <RegistrationButton modalUrl={registration.paths.newDiscount}>
            Apply discount
          </RegistrationButton>
        )}

        {hasDeleteButton &&
          registration.isCanceled &&
          registration.payments.length === 0 &&
          registration.refunds.length === 0 && (
            <RegistrationButton buttonClass="quiet-destroy-btn" onClick={handleDelete}>
              Delete registration
            </RegistrationButton>
          )}

        {hasCancelButton && registration.isCanceled && (
          <RegistrationButton buttonClass="quiet-create-btn" onClick={handleRestore}>
            Restore registration
          </RegistrationButton>
        )}

        {hasCancelButton && !registration.isCanceled && (
          <RegistrationButton onClick={handleCancel}>Cancel registration</RegistrationButton>
        )}

        {hasEmailButton && (
          <Menu>
            <MenuButton className="sm-btn quiet-btn btn2">
              Email
              <span data-reach-menu-button-toggle-icon>
                <Icon symbol="general#down-caret" />
              </span>
            </MenuButton>
            <MenuList css={{ whiteSpace: "nowrap" }}>
              <MenuLink data-modal-url={registration.paths.newEmail}>Send email</MenuLink>
              <MenuLink onClick={handleResendEmail}>Resend confirmation emails</MenuLink>
              {registration.isMissingAttendeeInfo && (
                <MenuLink data-modal-url={registration.paths.attendeeInfoEmail}>
                  Incomplete attendee info reminder
                </MenuLink>
              )}
              {registration.isBalanceDue && (
                <MenuLink data-modal-url={registration.paths.balanceDueEmail}>
                  Balance due reminder
                </MenuLink>
              )}
              {registration.isFormDue && (
                <MenuLink data-modal-url={registration.paths.formDueEmail}>
                  Incomplete external form reminder
                </MenuLink>
              )}
            </MenuList>
          </Menu>
        )}
        {hasFinalizeButton && isActingStaff && (!registration.isFinalized || hasInitialAttendees) && (
          <RegistrationButton buttonClass="ml-1 create-btn staff-only" onClick={handleFinalize}>
            <Icon symbol="general#check" />
            Finalize
          </RegistrationButton>
        )}
      </div>
    </div>
  )
}

SectionFooter.propTypes = {
  isActingStaff: PropTypes.bool,
  event: PropTypes.shape({
    isSimple: PropTypes.bool,
    paths: PropTypes.shape({
      show: PropTypes.string
    })
  }),
  registration: PropTypes.shape({
    id: PropTypes.number,
    createdAt: PropTypes.string,
    createdByName: PropTypes.string,
    payments: PropTypes.array,
    refunds: PropTypes.array,
    attendees: PropTypes.array,
    canceledOn: PropTypes.string,
    isCanceled: PropTypes.bool,
    isForFreeEvent: PropTypes.bool,
    isFinalized: PropTypes.bool,
    isFormDue: PropTypes.bool,
    isBalanceDue: PropTypes.bool,
    isMissingAttendeeInfo: PropTypes.bool,
    restoreAlertText: PropTypes.string,
    paths: PropTypes.shape({
      balanceDueEmail: PropTypes.string,
      formDueEmail: PropTypes.string,
      attendeeInfoEmail: PropTypes.string,
      newEmail: PropTypes.string,
      edit: PropTypes.string,
      newDiscount: PropTypes.string,
      show: PropTypes.string,
      confirmEmail: PropTypes.string,
      cancel: PropTypes.string,
      finalize: PropTypes.string
    })
  }),
  hasAttendeeButton: PropTypes.bool,
  hasDiscountButton: PropTypes.bool,
  hasDeleteButton: PropTypes.bool,
  hasCancelButton: PropTypes.bool,
  hasEmailButton: PropTypes.bool,
  hasFinalizeButton: PropTypes.bool
}

export default SectionFooter
